/**
 * Service for downloading all user data as a ZIP file
 * Modular service for data export functionality
 */

import { supabase } from '@config/supabase';
import { supabaseAWSService } from './supabase-aws.service';
import { useAuthStore } from '@stores/useAuthStore';

export type DownloadProgress = {
  stage: 'fetching' | 'downloading' | 'packaging' | 'complete';
  current: number;
  total: number;
  message: string;
};

/**
 * Download all user files as a ZIP archive
 * @param onProgress Optional callback to track download progress
 */
export async function downloadAllDataAsZip(
  onProgress?: (progress: DownloadProgress) => void
): Promise<void> {
  const user = useAuthStore.getState().user;
  if (!user?.id) {
    throw new Error('User not authenticated');
  }

  // Dynamic import of JSZip to avoid bundle bloat if not used
  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();

  try {
    // Stage 1: Fetch all user files
    onProgress?.({
      stage: 'fetching',
      current: 0,
      total: 0,
      message: 'Fetching file list...',
    });

    const { data: files, error: filesError } = await supabase
      .from('files')
      .select('id, name, original_name, s3_key, size, user_id')
      .eq('user_id', user.id)
      .eq('is_trashed', false);

    if (filesError) {
      throw new Error(`Failed to fetch files: ${filesError.message}`);
    }

    if (!files || files.length === 0) {
      throw new Error('No files found to download');
    }

    const totalFiles = files.length;
    onProgress?.({
      stage: 'downloading',
      current: 0,
      total: totalFiles,
      message: `Found ${totalFiles} file${totalFiles === 1 ? '' : 's'}. Starting download...`,
    });

    // Download files in batches to avoid memory issues
    const BATCH_SIZE = 5;
    let processedFiles = 0;
    let skippedFiles = 0;

    for (let i = 0; i < files.length; i += BATCH_SIZE) {
      const batch = files.slice(i, i + BATCH_SIZE);
      
      await Promise.all(
        batch.map(async (file) => {
          try {
            // Get download URL - handle files without s3_key (legacy files)
            if (!file.s3_key) {
              console.warn(`File ${file.name} has no s3_key, skipping`);
              skippedFiles++;
              return;
            }

            const downloadUrl = await supabaseAWSService.getSignedUrl(
              file.s3_key,
              file.user_id
            );

            // Fetch file
            const response = await fetch(downloadUrl);
            if (!response.ok) {
              console.warn(`Failed to download file ${file.name}:`, response.statusText);
              skippedFiles++;
              return;
            }

            const blob = await response.blob();
            
            // Use original_name if available, otherwise use name
            const fileName = file.original_name || file.name;
            
            // Handle duplicate filenames by adding a counter
            let finalFileName = fileName;
            let counter = 1;
            while (zip.file(finalFileName)) {
              const ext = fileName.substring(fileName.lastIndexOf('.'));
              const baseName = fileName.substring(0, fileName.lastIndexOf('.'));
              finalFileName = `${baseName}_${counter}${ext}`;
              counter++;
            }
            
            // Add to ZIP with original filename
            zip.file(finalFileName, blob);
            processedFiles++;

            // Update progress
            onProgress?.({
              stage: 'downloading',
              current: processedFiles,
              total: totalFiles,
              message: `Downloading files... ${processedFiles} of ${totalFiles} completed`,
            });
          } catch (error) {
            console.error(`Error downloading file ${file.name}:`, error);
            skippedFiles++;
            // Continue with other files even if one fails
          }
        })
      );
    }

    if (processedFiles === 0) {
      throw new Error('No files could be downloaded');
    }

    // Stage 3: Generate ZIP file
    onProgress?.({
      stage: 'packaging',
      current: processedFiles,
      total: processedFiles,
      message: `Packaging ${processedFiles} file${processedFiles === 1 ? '' : 's'} into ZIP archive...`,
    });

    const zipBlob = await zip.generateAsync({ 
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: { level: 6 }
    });
    
    // Stage 4: Complete - trigger download
    onProgress?.({
      stage: 'complete',
      current: processedFiles,
      total: processedFiles,
      message: `ZIP archive ready! ${processedFiles} file${processedFiles === 1 ? '' : 's'} included${skippedFiles > 0 ? ` (${skippedFiles} skipped)` : ''}`,
    });

    // Create download link
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fyle-data-export-${new Date().toISOString().split('T')[0]}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (error: any) {
    console.error('Error creating ZIP download:', error);
    throw error;
  }
}

